import { Fragment, useEffect, useState } from 'react'
import { Calendar, User, ToggleLeft, CalendarX, FileText, FileSpreadsheet } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import api from '../services/api'
import LookupSelect from '../components/LookupSelect'
import { useEmpresa } from '../contexts/EmpresaContext'
import { Linha } from './LancamentoDepositos'
import { SeletorMultiplo } from './RelatorioConsumoArla'

// Funcionários Afastados (WRELAFA / RRELAFA1 do ERP) — histórico de afastamentos (frota.motafx)

interface Afastado { motcod: string; nome: string; inicio: string; fim: string; afacod: number; motivo: string; usuario: string; obs: string }

const dataBr = (iso: string) => (iso ? iso.split('-').reverse().join('/') : '')
const isoLocal = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

export default function RelatorioFuncionariosAfastados() {
  const { empresaCod } = useEmpresa()
  const hoje = new Date()
  const [busca] = useSearchParams() // o cartão Pessoas do dashboard abre já com o período e gera na hora
  const dataUrl = (k: string) => (/^\d{4}-\d{2}-\d{2}$/.test(busca.get(k) ?? '') ? busca.get(k)! : '')
  const [dataIni, setDataIni] = useState(dataUrl('inicio') || isoLocal(new Date(hoje.getFullYear(), hoje.getMonth(), 1)))
  const [dataFim, setDataFim] = useState(dataUrl('fim') || isoLocal(new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0)))
  const [funcCod, setFuncCod] = useState('')
  const [funcNome, setFuncNome] = useState('')
  const [situacao, setSituacao] = useState('A')
  const [afastSel, setAfastSel] = useState<string[]>([]) // vazio = todos
  const [tipos, setTipos] = useState<{ cod: string; des: string }[]>([])
  const [res, setRes] = useState<Afastado[] | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [gerando, setGerando] = useState<'' | 'pdf' | 'excel'>('')
  const [erro, setErro] = useState('')

  useEffect(() => { if (busca.get('auto') === '1') gerar() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get('/afastamentos').then(r => setTipos(r.data.map((a: any) => ({ cod: String(a.afacod), des: `${a.afacod} — ${a.afades}` })))).catch(() => {})
  }, [])

  const params = () => ({ inicio: dataIni, fim: dataFim, motcod: funcCod, situacao, afastamentos: afastSel.join(','), empresa: empresaCod ?? 1 })

  async function gerar() {
    setErro('')
    setCarregando(true)
    try {
      const r = await api.get('/funcionarios/relatorio/afastados', { params: params() })
      setRes(r.data)
    } catch (e: any) {
      setRes(null)
      setErro(e?.response?.data?.error || 'Erro ao gerar relatório.')
    } finally {
      setCarregando(false)
    }
  }

  async function exportar(formato: 'pdf' | 'excel') {
    setErro('')
    setGerando(formato)
    try {
      const r = await api.get('/funcionarios/relatorio/afastados', { params: { ...params(), formato }, responseType: 'blob' })
      const tipoArq = formato === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      const url = URL.createObjectURL(new Blob([r.data], { type: tipoArq }))
      if (formato === 'pdf') {
        window.open(url, '_blank')
      } else {
        const a = document.createElement('a')
        a.href = url
        a.download = 'funcionarios-afastados.xlsx'
        a.click()
        URL.revokeObjectURL(url)
      }
    } catch {
      setErro(formato === 'pdf' ? 'Erro ao gerar PDF.' : 'Erro ao gerar Excel.')
    } finally {
      setGerando('')
    }
  }

  const th: React.CSSProperties = { padding: '0.5rem 0.6rem', fontSize: '0.68rem', fontWeight: 800, color: '#fff', background: '#1b3f7b', textAlign: 'left', whiteSpace: 'nowrap', textTransform: 'uppercase' }
  const td: React.CSSProperties = { padding: '0.4rem 0.6rem', fontSize: '0.82rem', textAlign: 'left', whiteSpace: 'nowrap' }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
      <div className="modal-box" style={{ width: '100%', maxWidth: 680, maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1a6ea8, #14547f)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 9, background: 'rgba(255,255,255,0.15)' }}>
              <CalendarX size={18} color="#fff" />
            </span>
            <div>
              <h2>Funcionários Afastados</h2>
              <div style={{ fontSize: '0.7rem', color: '#dbeafe', marginTop: 2 }}>Afastamentos lançados no período, com motivo e observação</div>
            </div>
          </div>
        </div>

        <div className="modal-corpo">
          <Linha label="Período" icon={Calendar}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <input type="date" value={dataIni} onChange={e => setDataIni(e.target.value)} style={{ maxWidth: 170 }} />
              <span style={{ fontSize: '0.8rem', color: '#57606f' }}>a</span>
              <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} style={{ maxWidth: 170 }} />
            </div>
          </Linha>

          <Linha label="Funcionário" icon={User}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <div style={{ minWidth: 300 }}>
                <LookupSelect endpoint="/funcionarios" extraParams={{ status: 'todos' }} codKey="motcod" descKey="motnome" tagKey="motcod"
                  codValue={funcCod} descValue={funcNome}
                  onSelect={(cod, desc) => { setFuncCod(cod); setFuncNome(desc) }}
                  placeholder="Todos" listWidth={320} />
              </div>
              {funcCod
                ? <button className="btn btn-secondary" style={{ padding: '0.25rem 0.6rem', fontSize: '0.72rem' }} onClick={() => { setFuncCod(''); setFuncNome('') }}>Todos</button>
                : <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>em branco = todos</span>}
            </div>
          </Linha>

          <Linha label="Situação" icon={ToggleLeft}>
            <select value={situacao} onChange={e => setSituacao(e.target.value)} style={{ maxWidth: 160 }}>
              <option value="A">Ativos</option>
              <option value="I">Inativos</option>
              <option value="">Todos</option>
            </select>
          </Linha>

          <Linha label="Afastamentos" icon={CalendarX}>
            <SeletorMultiplo opcoes={tipos} selecionados={afastSel} onChange={setAfastSel} placeholderBusca="Buscar afastamento..." />
          </Linha>

          {erro && (
            <div style={{ marginTop: '0.9rem', fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>
              {erro}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-primary" onClick={gerar} disabled={carregando}>{carregando ? 'Gerando...' : 'Gerar'}</button>
          <button className="btn" style={{ background: '#dc2626', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
            onClick={() => exportar('pdf')} disabled={!!gerando}>
            <FileText size={15} /> {gerando === 'pdf' ? 'Gerando PDF...' : 'PDF'}
          </button>
          <button className="btn" style={{ background: '#15803d', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
            onClick={() => exportar('excel')} disabled={!!gerando}>
            <FileSpreadsheet size={15} /> {gerando === 'excel' ? 'Gerando Excel...' : 'Excel'}
          </button>
        </div>
      </div>

      {res && (
        <div className="modal-box" style={{ width: '100%', maxWidth: 'none', maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
          <div className="modal-corpo" style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
            {res.length === 0 && <div style={{ textAlign: 'center', color: '#94a3b8', padding: '1.2rem' }}>Nenhum afastamento no período.</div>}

            {res.length > 0 && (
              <>
                <div style={{ overflowX: 'auto', border: '1px solid #bfdbfe', borderRadius: 8 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={th}>Código</th><th style={th}>Funcionário</th><th style={th}>Dt. Inicial</th><th style={th}>Dt. Final</th>
                        <th style={th}>Motivo do Afastamento</th><th style={th}>Usuário</th>
                      </tr>
                    </thead>
                    <tbody>
                      {res.map((l, i) => {
                        const bg = i % 2 ? '#f5f9fd' : '#fff'
                        return (
                          <Fragment key={`${l.motcod}-${l.inicio}-${i}`}>
                            <tr style={{ background: bg }}>
                              <td style={{ ...td, fontWeight: 600, borderBottom: l.obs ? 'none' : '1px solid #eef2f7' }}>{l.motcod}</td>
                              <td style={{ ...td, fontWeight: 600, borderBottom: l.obs ? 'none' : '1px solid #eef2f7' }}>{l.nome}</td>
                              <td style={{ ...td, borderBottom: l.obs ? 'none' : '1px solid #eef2f7' }}>{dataBr(l.inicio)}</td>
                              <td style={{ ...td, borderBottom: l.obs ? 'none' : '1px solid #eef2f7', color: l.fim ? undefined : '#b45309', fontWeight: l.fim ? undefined : 600 }}>
                                {l.fim ? dataBr(l.fim) : 'em aberto'}
                              </td>
                              <td style={{ ...td, borderBottom: l.obs ? 'none' : '1px solid #eef2f7' }}>{l.afacod} {l.motivo}</td>
                              <td style={{ ...td, borderBottom: l.obs ? 'none' : '1px solid #eef2f7' }}>{l.usuario}</td>
                            </tr>
                            {l.obs && (
                              <tr style={{ background: bg }}>
                                <td />
                                <td colSpan={5} style={{ ...td, whiteSpace: 'pre-wrap', fontSize: '0.78rem', color: '#475569', fontStyle: 'italic', paddingTop: 0, borderBottom: '1px solid #eef2f7' }}>{l.obs}</td>
                              </tr>
                            )}
                          </Fragment>
                        )
                      })}
                    </tbody>
                  </table>
                </div>

                <div style={{ borderRadius: 12, padding: '0.8rem 1.1rem', background: 'linear-gradient(135deg, #0f2a52, #1b3f7b)', color: '#fff', fontWeight: 800, letterSpacing: '0.02em' }}>
                  TOTAL DE AFASTAMENTOS: {res.length}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
